import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon, TrendingUp } from '@mui/icons-material';
import { modalStyles } from '../styles/modalStyles';
import PlanDetails from './PlanDetails';
import PlansModal from './PlansModal';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { USER_ROLES } from '../helpers/constants';
import { Plan, fetchPlans, fetchActivePlans } from '../services/companyService';
import { STORAGE_KEYS } from '../helpers/constants';
import { getZohoSubscriptionId } from '../helpers/storageUtils';
import { usePlanUpgrade } from '../hooks/usePlanUpgrade';

interface PlanDetailsModalProps {
  open: boolean;
  onClose: () => void;
  onPlanSelect?: (planId: string) => void;
  onUpgrade?: () => void;
}

const PlanDetailsModal: React.FC<PlanDetailsModalProps> = ({
  open,
  onClose,
  onPlanSelect,
  onUpgrade,
}) => {
  const muiTheme = useTheme();
  const { user } = useAuth();
  const isUserRole = user?.role === USER_ROLES.USER;
  const [plansModalOpen, setPlansModalOpen] = useState<boolean>(false);
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [activePlanCode, setActivePlanCode] = useState<string | null>(null);
  const [activePlanName, setActivePlanName] = useState<string | null>(null);

  const { handlePlanSelect: handlePlanUpgrade } = usePlanUpgrade({
    currentPlanCode: activePlanCode || undefined,
    currentPlanName: activePlanName || undefined,
    onError: (errorMsg) => {
      alert(`Failed to create payment page: ${errorMsg}. Please try again.`);
      setPlansModalOpen(true); // Reopen modal on error
    },
  });
  
  const handleClose = () => {
    onClose();
  };

  const handleUpgrade = () => {
    if (isUserRole) {
      return; // Prevent action for user role
    }
    
    if (onUpgrade) {
      onUpgrade();
    } else {
      // Open PlansModal
      setPlansModalOpen(true);
    }
  };

  // Handle plan selection from PlansModal
  const handlePlanSelectFromModal = async (planId: string) => {
    try {
      await handlePlanUpgrade(planId, availablePlans, () => setPlansModalOpen(false));
    } catch (error) {
      // Error handling is done in usePlanUpgrade hook
    }
  };

  // Fetch plans when PlansModal opens
  useEffect(() => {
    if (plansModalOpen && availablePlans.length === 0) {
      const loadPlans = async () => {
        try {
          const plansResponse = await fetchPlans();
          if (plansResponse.success) {
            setAvailablePlans(plansResponse.data);
          }
        } catch (err) {
          console.error('Error fetching plans:', err);
        }
      };
      loadPlans();
    }
  }, [plansModalOpen]);

  // Fetch active plan code when modal opens to identify current plan
  useEffect(() => {
    if (open && !activePlanCode) {
      const loadActivePlan = async () => {
        try {
          // Use REFRESH_TOKEN first like other working services (channelService, appsService, userService)
          const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || 
                       localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || 
                       undefined;
          const response = await fetchActivePlans(token);
          if (response.data) {
            setActivePlanCode(response.data.plan_code);
            setActivePlanName(response.data.plan_name);
          }
        } catch (err) {
          console.error('Error fetching active plan:', err);
        }
      };
      loadActivePlan();
    }
  }, [open, activePlanCode]);

  return (
    <Dialog
      id="plan-details-modal"
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          handleClose();
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          ...modalStyles.dialog['& .MuiDialog-paper'],
        },
      }}
    >
      <DialogTitle id="plan-details-modal-title" sx={modalStyles.dialogTitle}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          Current Plan Details
        </Typography>
        <IconButton
          id="plan-details-modal-close-btn"
          onClick={handleClose}
          sx={modalStyles.closeButton}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        id="plan-details-modal-content" 
        sx={{ 
          pt: 1.5, 
          px: 2, 
          pb: 1, 
          overflow: 'auto',
          maxHeight: '70vh',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: muiTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            '&:hover': {
              background: muiTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
            },
          },
        }}
      >
        <PlanDetails 
          onPlanSelect={onPlanSelect}
          showDetailedInfo={true}
          showUpgradeButton={false}
          onUpgrade={handleUpgrade}
        />
      </DialogContent>

      <DialogActions id="plan-details-modal-actions" sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          id="plan-details-close-btn"
          variant="outlined"
          onClick={handleClose}
          sx={{ 
            textTransform: 'none',
            fontSize: '0.875rem',
            px: 2,
            py: 0.5,
            borderColor: muiTheme.palette.divider,
            color: muiTheme.palette.text.secondary,
            '&:hover': {
              borderColor: muiTheme.palette.text.primary,
              color: muiTheme.palette.text.primary,
              backgroundColor: muiTheme.palette.action.hover
            }
          }}
        >
          Close
        </Button>
        <Tooltip 
          title={isUserRole ? "Please contact administrator to upgrade your plan" : ""}
          arrow
          placement="top"
        >
          <span>
            <Button
              id="plan-details-upgrade-btn"
              variant="contained"
              onClick={handleUpgrade}
              color="primary"
              disabled={isUserRole}
              startIcon={<TrendingUp />}
              sx={{
                textTransform: 'none',
                fontSize: '0.875rem',
                px: 2,
                py: 0.5,
              }}
            >
              Upgrade Plan
            </Button>
          </span>
        </Tooltip>
      </DialogActions>

      {/* Plans Modal */}
      <PlansModal
        open={plansModalOpen}
        onClose={() => setPlansModalOpen(false)}
        onPlanSelect={handlePlanSelectFromModal}
        currentPlanCode={activePlanCode ?? undefined}
        currentPlanId={
          activePlanCode
            ? availablePlans.find((p) => p.planCode === activePlanCode)?.id
            : undefined
        }
        plans={availablePlans}
        autoFetch={false}
        showCurrentPlanBadge={true}
        gridColumns={{ xs: 1, md: 2, lg: 3 }}
        subscriptionId={getZohoSubscriptionId() || undefined}
      />
    </Dialog>
  );
};

export default PlanDetailsModal;

